import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, ModalFooter } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { leadRetriesReportDetailsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallexLoader from '../../../components/CallexLoader';
import { formatPayloadDate, convertToMask, fileDownload } from '../../../utils/common';
import * as Icon from 'react-feather';
import config from '../../../config';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import { CSVLink } from 'react-csv';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission } from '../../../utils/common';
import { startOfDay, endOfDay } from 'date-fns';

const LeadRetriesReportDetailsModal = ({ isOpen, onCloseModal, selectedLead, filters }) => {
  const dispatch = useDispatch();
  const [detailsData, setDetailsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [detailsHeader, setDetailsHeader] = useState({
    default: [],
    crm_fields: []
  });
  const [audioDetails, setAudioDetails] = useState(null);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const excelRef = React.useRef();
  const userRolePermission = useUserRolePermission();

  const fetchDetails = async () => {
    if (!selectedLead) return;
    setIsLoading(true);
    try {
      const leadDate = new Date(selectedLead.date);
      const formattedStartDate = formatPayloadDate(startOfDay(leadDate));
      const formattedEndDate = formatPayloadDate(endOfDay(leadDate));
      const payload = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        archiveId: filters.archiveId,
        crm_id: filters.crm_id,
        process_id: selectedLead.process_id,
        destination_number: selectedLead.destination_number,
        listId: filters.listId
      };

      const response = await dispatch(leadRetriesReportDetailsGet(payload)).unwrap();
      if (response) {
        setDetailsData(response?.data || []);
        setDetailsHeader({
          default: response?.header?.default || [],
          crm_fields: response?.header?.crm_fields || []
        });
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDetails();
    }
  }, [isOpen, selectedLead]);

  useEffect(() => {
    if (isCsvDownload && detailsData.length > 0) {
      excelRef?.current?.link?.click();
      setIsCsvDownload(false);
    }
  }, [isCsvDownload]);

  const handleExportCSV = () => {
    const headers = (detailsHeader.default || [])
      .concat(detailsHeader.crm_fields || [])
      .map((val) => ({
        label: val.label,
        key: val.name
      }));
    setCsvHeaderData(headers);
    setCsvReportData(detailsData);
    setIsCsvDownload(true);
  };

  const isExportDisabled = isLoading || detailsData.length === 0;

  return (
    <>
      <Modal isOpen={isOpen} size="xl" style={{ maxWidth: "85vw" }}>
        <ModalHeader className='lead-retries-report-hedaer'>
          <span>Lead Retries Details</span>
          {checkModulePermission('is_export', 'Lead Retries Report', userRolePermission) && (
            <div className="d-flex align-items-center">
              <div
                className={`bg-success rounded-2 d-flex justify-content-center align-items-center ${isExportDisabled ? 'callex-disabled' : ''
                  }`}
                style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                onClick={handleExportCSV}>
                <Icon.Download size={20} className="text-white" />
              </div>
              {isCsvDownload && (
                <CSVLink
                  data={csvReportData}
                  headers={csvHeaderData}
                  filename={`Lead_Retries_Details_${selectedLead?.destination_number}.csv`}
                  ref={excelRef}
                  className="hiddenExcel"
                  target="_blank"
                />
              )}
            </div>
          )}
        </ModalHeader>
        <ModalBody className="p-0" style={{ height: 'calc(100vh - 190px)' }}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <CallexLoader />
            </div>
          ) : detailsData.length > 0 ? (
            <div className="daily-report-table h-100">
              <table className="report-list-table">
                <thead>
                  <tr className="title">
                    <th colSpan={detailsHeader?.default?.length}>Default</th>
                  </tr>
                  <tr>
                    {detailsHeader?.default?.map((val, index) => (
                      <th key={index}>{val.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailsData.map((val, index) => (
                    <tr key={index}>
                      {detailsHeader?.default?.map((header, idx) => {
                        if (header.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center">
                              {val[header.name] ? (
                                <div className="d-flex gap-2 justify-content-center">
                                  <Icon.PlayCircle
                                    size={16}
                                    onClick={() =>
                                      setAudioDetails({
                                        ...val,
                                        src: `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      })
                                    }
                                    className="cursor-pointer text-info"
                                  />
                                  <Icon.Download
                                    size={16}
                                    className="cursor-pointer"
                                    onClick={() => {
                                      fileDownload(
                                        `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        let content = val[header.name] || '-';
                        if (header.name === 'crm_number' && process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true') {
                          content = convertToMask(content);
                        }
                        return <td key={idx}>{content}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {detailsHeader?.crm_fields?.length > 0 && (
                <table className="report-list-table">
                  <thead>
                    <tr className="title">
                      <th colSpan={detailsHeader?.crm_fields?.length}>CRM</th>
                    </tr>
                    <tr>
                      {detailsHeader?.crm_fields?.map((val, index) => (
                        <th key={index}>{val.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailsData.map((val, index) => (
                      <tr key={index}>
                        {detailsHeader?.crm_fields?.map((header, idx) => (
                          <td key={idx}>{val[header.name] || '-'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              No details available
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Button className="px-5 bg-danger border-danger" onClick={onCloseModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </>
  );
};

LeadRetriesReportDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  selectedLead: PropTypes.object,
  filters: PropTypes.object.isRequired
};

export default LeadRetriesReportDetailsModal;
